'use client';

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { signInWithGoogle as startGoogleOAuth } from '@/lib/googleAuth';
import { onOAuthReturn } from '@/lib/oauthRetry';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}



interface Props {
  onSwitchToSignup: () => void;
}

export default function LoginForm({ onSwitchToSignup }: Props) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  // If the user cancels the Google flow and comes BACK to this page (browser
  // Back / bfcache restore), restore the button — otherwise it stays stuck on
  // "Redirecting…" and disabled until the app is fully reopened.
  useEffect(() => onOAuthReturn(() => setIsGoogleLoading(false)), []);

  const signInWithGoogle = async () => {
    if (isGoogleLoading) return; // never start two OAuth redirects at once
    setIsGoogleLoading(true);
    try {
      // This redirects the current page to Google — the callback will
      // handle onboarding checks and redirect appropriately.
      await startGoogleOAuth('/');
      // If the redirect did NOT navigate (blocked, aborted by the browser),
      // restore the button instead of leaving a permanent loading state.
      setIsGoogleLoading(false);
    } catch (error) {
      setError('root', {
        message: error instanceof Error ? error.message : 'Could not start Google sign-in.',
      });
      setIsGoogleLoading(false);
    }
  };

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email, password: data.password }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        const msg = result.error || 'Unable to sign in.';
        if (msg.toLowerCase().includes('email not confirmed')) {
          throw new Error('Please confirm your email address before signing in. Check your inbox for the confirmation link.');
        }
        throw new Error(msg);
      }
      toast.success('Welcome back!', { duration: 3000 });
      // Redirect to the quiz if the user hasn't completed onboarding yet.
      // This checks onboarding_completed (quiz state), NOT profile field count.
      try {
        const profileRes = await fetch('/api/profile', { cache: 'no-store' });
        if (profileRes.ok) {
          const profile = await profileRes.json();
          if (Number(profile.onboarding_completed) !== 1) {
            router.push('/onboarding');
            return;
          }
        }
      } catch {
        // Non-fatal — proceed to dashboard
      }
      router.push('/');
    } catch (error) {
      setError('root', {
        message: error instanceof Error ? error.message : 'Unable to sign in.',
      });
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground">Welcome back</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Sign in to continue your scholarship journey
        </p>
      </div>

      {/* Social auth */}
      <div className="mb-5">
        <button
          type="button"
          onClick={signInWithGoogle}
          disabled={isGoogleLoading}
          className="w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl border border-border bg-card text-sm font-semibold text-foreground hover:bg-muted transition-all active:scale-[0.98] disabled:opacity-60"
          style={{ minHeight: '48px' }}
        >
          {isGoogleLoading ? (
            <svg viewBox="0 0 24 24" className="w-4 h-4 animate-spin fill-none stroke-current stroke-2">
              <path d="M21 12a9 9 0 1 1-6.219-8.56" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
          )}
          {isGoogleLoading ? 'Redirecting…' : 'Continue with Google'}
        </button>
      </div>

      <div className="relative flex items-center mb-5">
        <div className="flex-1 border-t border-border" />
        <span className="px-3 text-xs text-muted-foreground bg-background">
          or sign in with email
        </span>
        <div className="flex-1 border-t border-border" />
      </div>

      {/* Root error */}
      {errors.root && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2">
          <svg
            viewBox="0 0 24 24"
            className="w-4 h-4 fill-none stroke-danger stroke-2 flex-shrink-0 mt-0.5"
          >
            <circle cx="12" cy="12" r="10" strokeLinecap="round" strokeLinejoin="round" />
            <line x1="12" y1="8" x2="12" y2="12" strokeLinecap="round" strokeLinejoin="round" />
            <line x1="12" y1="16" x2="12.01" y2="16" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <p className="text-sm text-danger">{errors.root.message}</p>
        </div>
      )}

      {/* Email */}
      <div className="mb-4">
        <label htmlFor="login-email" className="block text-sm font-semibold text-foreground mb-1.5">
          Email address
        </label>
        <input
          id="login-email"
          type="email"
          autoComplete="email"
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Enter a valid email address',
            },
          })}
          className={`input-base ${errors.email ? 'border-danger focus:ring-danger' : ''}`}
          placeholder="you@example.com"
          aria-describedby={errors.email ? 'login-email-error' : undefined}
          aria-invalid={!!errors.email}
        />
        {errors.email && (
          <p id="login-email-error" className="mt-1.5 text-xs text-danger flex items-center gap-1">
            <svg
              viewBox="0 0 24 24"
              className="w-3 h-3 fill-none stroke-current stroke-2 flex-shrink-0"
            >
              <circle cx="12" cy="12" r="10" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="12" y1="8" x2="12" y2="12" strokeLinecap="round" />
              <line x1="12" y1="16" x2="12.01" y2="16" strokeLinecap="round" />
            </svg>
            {errors.email.message}
          </p>
        )}
      </div>

      {/* Password */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <label htmlFor="login-password" className="block text-sm font-semibold text-foreground">
            Password
          </label>
          <button
            type="button"
            className="text-xs text-primary font-medium hover:text-blue-700 transition-colors"
          >
            Forgot password?
          </button>
        </div>
        <div className="relative">
          <input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 6, message: 'Password must be at least 6 characters' },
            })}
            className={`input-base pr-11 ${errors.password ? 'border-danger focus:ring-danger' : ''}`}
            placeholder="Your password"
            aria-describedby={errors.password ? 'login-password-error' : undefined}
            aria-invalid={!!errors.password}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-3.5 flex items-center text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-none stroke-current stroke-2">
                <path
                  d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <line x1="1" y1="1" x2="23" y2="23" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-none stroke-current stroke-2">
                <path
                  d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        </div>
        {errors.password && (
          <p
            id="login-password-error"
            className="mt-1.5 text-xs text-danger flex items-center gap-1"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-3 h-3 fill-none stroke-current stroke-2 flex-shrink-0"
            >
              <circle cx="12" cy="12" r="10" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="12" y1="8" x2="12" y2="12" strokeLinecap="round" />
              <line x1="12" y1="16" x2="12.01" y2="16" strokeLinecap="round" />
            </svg>
            {errors.password.message}
          </p>
        )}
      </div>

      {/* Remember me */}
      <div className="flex items-center gap-2 mb-6">
        <input
          id="remember-me"
          type="checkbox"
          {...register('rememberMe')}
          className="w-4 h-4 rounded border-input accent-primary cursor-pointer"
        />
        <label htmlFor="remember-me" className="text-sm text-muted-foreground cursor-pointer">
          Keep me signed in for 30 days
        </label>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-base"
        style={{ minHeight: '48px' }}
      >
        {isLoading ? (
          <>
            <svg
              viewBox="0 0 24 24"
              className="w-4 h-4 animate-spin fill-none stroke-current stroke-2"
            >
              <path d="M21 12a9 9 0 1 1-6.219-8.56" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Signing in…
          </>
        ) : (
          'Sign In'
        )}
      </button>

      <p className="text-center text-sm text-muted-foreground mt-5">
        Don&apos;t have an account?{' '}
        <button
          type="button"
          onClick={onSwitchToSignup}
          className="text-primary font-semibold hover:text-blue-700 transition-colors"
        >
          Create one free
        </button>
      </p>
    </form>
  );
}
